import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {animate, state, transition, trigger, style} from '@angular/animations';
import {AbstractComponent} from "@components";
import {
  Country,
  EventItem,
  EventStatus,
  League,
  Lookups,
  Organiser
} from "@model";
import {
  AuthenticationService, LeagueService, LoaderService,
  TOAST_STATE,
  ToastService
} from "@services";
import {max_length_255, required} from "@utility";
import {LeagueDetailsComponentType} from "../admin-league-details.component";

@Component({
  selector: 'app-league-basic-data',
  templateUrl: './league-basic-data.component.html',
  styleUrls: ['./league-basic-data.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})
export class LeagueBasicDataComponent extends AbstractComponent implements OnInit {
  @Input() disableEditing: boolean = false;
  @Input() currentLeague: League | undefined;
  @Input() leagueDetailsComponentType: LeagueDetailsComponentType | undefined;
  @Input() statuses: Lookups[] = [];
  @Input() organisers: Organiser[] = [];
  @Input({required:true}) sports: Lookups[] = [];
  @Input({required:true}) countries: Country[] = [];
  @Input({required:true}) languages: Lookups[] = [];
  @Input() userRole: String | undefined;
  @Output() onLeagueChange: EventEmitter<League> = new EventEmitter<League>();

  currentDate: Date = new Date();
  countriesOptions: Map<number, string> = new Map<number, string>();

  inputForm = new FormGroup({
    organiserId: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    status: new FormControl(),
    leagueDates: new FormControl(),
    imageMedium: new FormControl(),
    imageIcon: new FormControl(),
    defaultLanguage: new FormControl(),
    extraLanguages: new FormControl(),
    description: new FormControl(),
    rules: new FormControl(),
    sportId: new FormControl(),
    countries: new FormControl(),
    maxNumberOfEvents: new FormControl(),
  });

  constructor(private toastService: ToastService, public translocoService: TranslocoService,
              public authenticationService: AuthenticationService, private leagueService: LeagueService,
              public loaderService: LoaderService) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.subscribeToDefaultLangChange();
    this.setCurrentLeagueData();
    this.countriesOptions = this.initCountriesOptions();
  }

  initCountriesOptions(): Map<number, string> {
    let countriesOptions: Map<number, string> = new Map<number, string>();
    if ((this.countries?.length ?? 0) > 0) {
      for (let i = 0; i < this.countries!.length; i++) {
        countriesOptions.set(this.countries[i].id!, this.countries[i].name ?? "");
      }
    }
    return countriesOptions;
  }

  private initForm(): void {
    this.inputForm.controls.organiserId.addValidators([required]);
    this.inputForm.controls.defaultLanguage.addValidators([required]);
    this.inputForm.controls.name.addValidators([required, max_length_255]);
    this.inputForm.controls.nameEng.addValidators([max_length_255]);
    this.inputForm.controls.nameIta.addValidators([max_length_255]);
    this.inputForm.controls.nameSlo.addValidators([max_length_255]);
    this.inputForm.controls.leagueDates.addValidators([required]);
    this.inputForm.controls.status.addValidators([required]);
    this.inputForm.controls.sportId.addValidators([required]);
    this.inputForm.controls.maxNumberOfEvents.addValidators([required]);
    this.inputForm.controls.countries.addValidators([required]);
    this.inputForm.controls.imageIcon.addValidators([required]);
    this.inputForm.controls.imageMedium.addValidators([required]);
    if (this.leagueDetailsComponentType === LeagueDetailsComponentType.COPY) {
      this.inputForm.controls.status.disable();
    }
    if (this.organisers.length === 1) {
      this.inputForm.controls.organiserId.setValue(this.organisers[0].id);
    }
  }

  private subscribeToDefaultLangChange() {
    this.subscriptions.push(this.inputForm.controls.defaultLanguage.valueChanges.subscribe((langId: number) => {
      if (langId) {
        if (Array.isArray(this.inputForm.controls.extraLanguages.value)) {
          let extraLanguages: any[] = this.inputForm.controls.extraLanguages.value ? [...this.inputForm.controls.extraLanguages.value] : [];
          let index: number = extraLanguages.indexOf(langId, 0);
          if (index > -1) {
            extraLanguages.splice(index, 1);
            this.inputForm.controls.extraLanguages.setValue(extraLanguages);
          }
        }
      }
    }));
  }

  private setCurrentLeagueData(): void {
    if (this.currentLeague) {
      this.inputForm.patchValue(this.currentLeague);
      if (this.currentLeague.organiser?.id) {
        this.inputForm.controls.organiserId.setValue(this.currentLeague.organiser?.id);
      }
      this.leagueService.getFilteredLeagueStatuses().subscribe((statuses: Lookups[]) => {
        this.statuses = statuses
      })
      this.inputForm.controls.leagueDates.setValue({from: this.currentLeague.start, to: this.currentLeague.end});

      if (!this.currentLeague.status) {
        this.inputForm.controls.status.setValue(EventStatus.INACTIVE);
      }
      this.inputForm.updateValueAndValidity();
    }
  }

  onSave(): void {
    this.markAllAsTouched(this.inputForm)
    if (!this.inputForm.valid) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("league.details.error.form"))
      return;
    } else {
      let league: League = this.getLeagueFields();
      if (this.currentLeague !== undefined) {
        if (this.leagueDetailsComponentType === LeagueDetailsComponentType.COPY) {
          /*          this.eventService.copyEvent(eventDetails, () => {
                      this.toastService.showToast(TOAST_STATE.success,
                        this.translocoService.translate("app.common.success.event.basic.data.edit"))
                    }, () => {
                      this.toastService.showToast(TOAST_STATE.danger,
                        this.translocoService.translate("app.common.server.error"))
                    }).subscribe((eventItem: EventItem | null) => {
                      if (eventItem) {
                        this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: eventItem.id}});
                      }
                    });*/
        } else {
          this.loaderService.startLoading();
          this.leagueService.updateLeague(league, (league: League) => {
            this.loaderService.dismissLoading();
            this.currentLeague = league;
            this.onLeagueChange.emit(this.currentLeague);
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("league.details.success.basic.data.edit"))
          }, () => {
            this.loaderService.dismissLoading();
            this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("app.common.server.error"))
          });
        }
      }
    }
  }

  getLeagueFields(): EventItem {
    this.checkValidators(this.inputForm);
    let league: League = {...this.currentLeague}
    league.organiser = this.organisers?.filter((organiser: Organiser): boolean => organiser.id === this.inputForm.value.organiserId)[0];
    league.name = this.inputForm.value.name;
    league.nameEng = this.inputForm.value.nameEng;
    league.nameIta = this.inputForm.value.nameIta;
    league.nameSlo = this.inputForm.value.nameSlo;
    league.status = this.inputForm.value.status;
    league.start = this.inputForm.value.leagueDates?.from;
    league.end = this.inputForm.value.leagueDates?.to;
    league.defaultLanguage = this.inputForm.value.defaultLanguage;
    league.extraLanguages = this.inputForm.controls.extraLanguages.value;
    league.imageIcon = this.inputForm.value.imageIcon;
    league.imageMedium = this.inputForm.value.imageMedium;
    league.description = this.inputForm.value.description;
    league.rules = this.inputForm.value.rules;
    league.sportId = this.inputForm.value.sportId;
    league.maxNumberOfEvents = this.inputForm.value.maxNumberOfEvents;
    league.countries = this.inputForm.value.countries;
    league.image = undefined;
    league.imageSmall = undefined;
    league.imageMini = undefined;
    return league;
  }

  getOptions(): Map<number, string> {
    const optionsMap: Map<number, string> = new Map<number, string>();
    this.languages.map((language: Lookups): void => {
      if (language.id && language.id !== this.inputForm.controls.defaultLanguage.value) {
        optionsMap.set(language.id, (language?.code) ? this.translocoService.translate('app.common.language.' + language.code) : "")
      }
    });
    return optionsMap;
  }

  imageChange(image: string | undefined): void {
    this.inputForm.controls.imageMedium.setValue(image);
  }

  logoChange(image: string | undefined): void {
    this.inputForm.controls.imageIcon.setValue(image);
  }
  onDescriptionChangeEvent(content: string | undefined) {
    this.inputForm.controls.description.setValue(content);
  }

  onRulesChangeEvent(content: string | undefined) {
    this.inputForm.controls.rules.setValue(content);
  }
}


